import { computeDragSourceMode } from '../../permissions/modes';
import type { FileManagerPermission } from '@/composeables/file-manager-capabilities';
import { isSynthetic, type FileSystemItem } from '../../types';
import { getParentPath } from '../../utils';
import type { FileManagerMount } from '../core/useFileManagerMounts';
import { transferPolicyFor, type TransferPolicy } from '../transfer/transferPolicy';

/**
 * Pure: may this item be picked up as a drag source. The one place both drag surfaces ask — the grid
 * (useFileGridInteractions' canDragItem) and the sidebar tree (DirectoryItem's own canDrag) — which
 * previously each spelled out the same two conditions and were free to drift apart.
 *
 * Two conditions, and they are different in kind. The permission half is EITHER transfer verb (see
 * computeDragSourceMode). The item-kind half is structural: above the mounts every row is synthetic —
 * a document library or an implied namespace level, not a driveItem — and there is no PATCH or copy
 * that relocates one, so it is never draggable at any tier.
 */
export const canDragSource = (
  item: FileSystemItem,
  permissions: FileManagerPermission[],
): boolean =>
  computeDragSourceMode(permissions) === 'enabled' && !isSynthetic(item);

/**
 * Whether a drop can happen and, when it can, WHAT it means — the policy is carried on the verdict
 * rather than re-derived by the caller, so the drop handler, the permission gate and the drag cursor
 * all read the same evaluation.
 *
 * 'no-destination' is what remains of the old 'cross-library' refusal now that crossing a mount
 * boundary within a project is a supported copy: the only drop with no drive to land on is one onto the
 * virtual root or a namespace level (synthetic listings of the mount table, not folders) or onto an
 * unknown/inaccessible mount. Unlike the old cross-library case it needs no explanation toast — a
 * namespace row and the breadcrumb home button are visibly not folders, and they show a 'none' drag
 * cursor.
 *
 * 'cross-scope' is a drop between two *projects* — see scopeOf. Also silent: the two rows sit in
 * visibly different subtrees, and the 'none' cursor is the affordance.
 */
export type DropVerdict =
  | { kind: 'ok'; policy: TransferPolicy }
  | { kind: 'invalid' }
  | { kind: 'no-destination' }
  | { kind: 'cross-scope' };

/**
 * Pure: which scope a mount belongs to — its prefix minus its own final segment. `''` for a depth-1
 * mount, so on the project page every mount shares one scope and no drop's verdict can change.
 *
 * Inferring anything from a prefix is inferring it from a *display* path, which is the antipattern
 * transferPolicy's own doc comment warns against. What makes it acceptable here, and only here, is an
 * asymmetry worth stating out loud: prefix inference is unsafe for GRANTING and safe for REFUSING. A
 * wrong refusal costs the user a drag; a wrong permit is an irreversible disclosure of content into
 * another project's document library, under another project's ACL.
 *
 * So this is deliberately not the mechanism for *allowing* cross-project transfer. If that is ever
 * wanted, the upgrade path is a host-supplied opaque `scope` on the mount — compared for inequality
 * the same way, but with a disclosure sentence naming the real destination project — rather than
 * loosening this inference.
 */
export const scopeOf = (mount: FileManagerMount): string =>
  mount.prefix.slice(0, mount.prefix.lastIndexOf('/'));

// `mountOf` is injected rather than resolved internally: this keeps dragGuards pure and fake-free in
// tests, while the real caller (useDropTarget) takes it straight off useFileManagerMounts' own
// `mountAt`, itself a longest-prefix match over the mount table. Returns undefined for any path
// outside every mount.
//
// Deliberately still knows nothing about permissions: whether the resulting verb is *allowed* is a
// separate question the caller asks once it knows which verb it is.
export const evaluateDrop = (
  targetPath: string,
  items: FileSystemItem[],
  mountOf: (path: string) => FileManagerMount | undefined,
): DropVerdict => {
  if (items.length === 0) return { kind: 'invalid' };

  const destination = mountOf(targetPath);
  if (!destination) return { kind: 'no-destination' };

  // One source mount for the whole selection, asserted rather than assumed: two source mounts would
  // mean two policies at once, and a drop can only have one. Unreachable in practice — a grid drag
  // comes from a single folder's listing, a tree drag is a single row, and a mount itself is never a
  // drag source (canDragItem / DirectoryItem's own `canDrag`).
  const source = mountOf(items[0].path);
  if (!source || items.some((item) => mountOf(item.path)?.key !== source.key)) {
    return { kind: 'invalid' };
  }

  // Refused, not copied (D8): crossing a mount boundary inside one project is a supported copy, but
  // crossing into another project is not a thing anyone asked for, and doing it silently would put
  // content under an unrelated ACL. See scopeOf for why inferring the scope from the prefix is sound
  // for a refusal specifically.
  if (scopeOf(source) !== scopeOf(destination)) return { kind: 'cross-scope' };

  // Unchanged, and still correct now that the target can be on another mount: each of these can only
  // ever be true within one mount, since paths under different mounts differ from their first
  // segment onwards.
  const isInvalid = items.some((item) => {
    if (targetPath === item.path) return true; // dropping onto itself
    if (targetPath === getParentPath(item.path)) return true; // already there
    // dropping a folder into its own descendant
    return item.type === 'directory' && targetPath.startsWith(`${item.path}/`);
  });
  if (isInvalid) return { kind: 'invalid' };

  return { kind: 'ok', policy: transferPolicyFor(source, destination) };
};

// Kept as the boolean every existing drop-target/highlight call site already asks for — only the
// callers that need the verb (to pick a permission gate, a drag cursor, or a mutation) reach for
// evaluateDrop().
export const canDropAt = (
  targetPath: string,
  items: FileSystemItem[],
  mountOf: (path: string) => FileManagerMount | undefined,
): boolean => evaluateDrop(targetPath, items, mountOf).kind === 'ok';
